import { useQuery } from "@tanstack/react-query";
import { NavLink } from "react-router-dom";

import {
  AuditIcon,
  CasesIcon,
  CustomersIcon,
  type IconProps,
  MessagesIcon,
  PipelineIcon,
  SettingsIcon,
  TasksIcon,
  TodayIcon,
} from "./icons";
import { type ApiUser, getOverdueTaskCount } from "../lib/api";

interface NavItemDef {
  to: string;
  label: string;
  icon: (props: IconProps) => JSX.Element;
  end?: boolean;
  adminOnly?: boolean;
  showOverdue?: boolean;
}

const NAV: ReadonlyArray<NavItemDef> = [
  { to: "/", label: "今日やること", icon: TodayIcon, end: true, showOverdue: true },
  { to: "/tasks", label: "タスク", icon: TasksIcon },
  { to: "/cases", label: "案件一覧", icon: CasesIcon },
  { to: "/customers", label: "顧客", icon: CustomersIcon },
  { to: "/pipeline", label: "パイプライン", icon: PipelineIcon },
  { to: "/messages", label: "メッセージ", icon: MessagesIcon },
  { to: "/audit-logs", label: "監査ログ", icon: AuditIcon, adminOnly: true },
  { to: "/settings", label: "設定", icon: SettingsIcon },
];

interface Props {
  user: ApiUser;
  open: boolean;
  onClose: () => void;
  onPrefetch: (path: string) => void;
}

export function Sidebar({ user, open, onClose, onPrefetch }: Props): JSX.Element {
  const items = NAV.filter((item) => item.adminOnly !== true || user.role === "admin");
  return (
    <>
      {open && (
        <button
          type="button"
          aria-label="メニューを閉じる"
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-60 flex-col bg-sidebar text-sidebar-text transition-transform duration-200 md:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <Brand />
        <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="メインメニュー">
          <ul className="space-y-1">
            {items.map((item) => (
              <li key={item.to}>
                <NavItem item={item} onNavigate={onClose} onPrefetch={onPrefetch} />
              </li>
            ))}
          </ul>
        </nav>
        <p className="px-5 py-4 text-[11px] leading-relaxed text-sidebar-text/60">
          送信・契約・金額の重要操作は
          <br />
          必ず人間が確認します
        </p>
      </aside>
    </>
  );
}

function Brand(): JSX.Element {
  return (
    <div className="flex h-16 items-center gap-2.5 px-5">
      <span className="flex h-8 w-8 rotate-45 items-center justify-center rounded-md bg-sidebar-accent" aria-hidden>
        <span className="-rotate-45 text-sm font-bold text-sidebar">OS</span>
      </span>
      <span className="text-base font-bold tracking-tight text-sidebar-text-strong">共通OS</span>
    </div>
  );
}

function NavItem({
  item,
  onNavigate,
  onPrefetch,
}: {
  item: NavItemDef;
  onNavigate: () => void;
  onPrefetch: (path: string) => void;
}): JSX.Element {
  const Icon = item.icon;
  const { data: overdue = 0 } = useQuery({
    queryKey: ["overdue-count"],
    queryFn: getOverdueTaskCount,
    staleTime: 60_000,
    enabled: item.showOverdue === true,
  });
  const badge = item.showOverdue === true ? overdue : 0;
  const prefetch = (): void => onPrefetch(item.to);
  return (
    <NavLink
      to={item.to}
      end={item.end ?? false}
      onClick={onNavigate}
      onMouseEnter={prefetch}
      onFocus={prefetch}
      onTouchStart={prefetch}
      className={({ isActive }) =>
        `relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
          isActive
            ? "bg-sidebar-active text-sidebar-text-strong"
            : "text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-strong"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute inset-y-1.5 left-0 w-1 rounded-r bg-sidebar-accent" aria-hidden />
          )}
          <Icon className="h-5 w-5 shrink-0" />
          <span className="flex-1 truncate">{item.label}</span>
          {badge > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-danger px-1.5 text-[11px] font-semibold text-white">
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
